/**
 * Display formatters used across the Reimbursements list and detail views.
 */

import { format, isValid, parse } from 'date-fns'

export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '$0.00'
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num)) return '$0.00'
  if (num < 0) {
    return `- ${Math.abs(num).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
  }
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/** Formats totals values matching Angular `defaultData?.total_count?.xxx ? (... | amount) : '$0'` */
export function formatTotalsCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '' || value === 0 || value === '0') return '$0'
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num) || num === 0) return '$0'
  return formatCurrency(num)
}

export function formatNumeric(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0'
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString('en-US')
}

/**
 * MM/dd/yyyy — matches Angular `date:'MM/dd/yyyy'` pipe
 */
export function formatDate(input: string | number | Date | null | undefined): string {
  if (!input) return ''
  const d = toDate(input)
  if (Number.isNaN(d.getTime())) return ''
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yyyy = String(d.getFullYear())
  return `${mm}/${dd}/${yyyy}`
}

/** Angular Liberty Internal list: `date:'yyyy-MM-dd hh:mm:ss a'` */
export function formatLibertyInternalNoteTimestamp(
  input: string | number | Date | null | undefined,
): string {
  if (input == null || input === '') return ''
  const d = toDate(input as string | number | Date)
  if (Number.isNaN(d.getTime())) return String(input)
  return format(d, 'yyyy-MM-dd hh:mm:ss a')
}

/** Formats timestamps for export status to `yyyy-MM-dd HH:mm:ss` */
export function formatExportTimestamp(
  input: string | number | Date | null | undefined,
): string {
  if (input == null || input === '') return ''
  const d = toDate(input as string | number | Date)
  if (Number.isNaN(d.getTime())) return String(input)
  return format(d, 'yyyy-MM-dd HH:mm:ss')
}

/**
 * Just the year — matches Angular `date:'yyyy'` pipe
 */
export function formatYear(input: string | number | Date | null | undefined): string {
  if (!input) return ''
  const d = toDate(input)
  if (Number.isNaN(d.getTime())) return ''
  return String(d.getFullYear())
}

/**
 * yyyy-MM-dd — used for API payloads
 */
export function toApiDate(input: Date | null | undefined): string {
  if (!input) return ''
  if (Number.isNaN(input.getTime())) return ''
  const yyyy = input.getFullYear()
  const mm = String(input.getMonth() + 1).padStart(2, '0')
  const dd = String(input.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Parse `yyyy-MM-dd` as a **local** calendar date. Avoids `new Date('yyyy-MM-dd')`, which is UTC and
 * shifts the calendar day in US timezones (same pitfall the resident list avoids with `parseResidentApiDate`).
 */
export function parseLocalApiDate(
  value: string | null | undefined,
): Date | null {
  if (value == null || String(value).trim() === '') return null
  const s = String(value).trim()
  const parsed = parse(s, 'yyyy-MM-dd', new Date())
  if (isValid(parsed)) return parsed
  const fallback = new Date(s)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

/**
 * Parses `_MMDDYYYY_HHMMSS_` embedded in policy-declaration filenames.
 * Matches Angular `PolicyDeclarationFilesPopupComponent.getFormattedDateFromFilename`.
 * Returns `MM-DD-YYYY HH:MM:SS`, or null when the pattern is absent.
 */
export { formatTimestampFromPolicyDeclarationFilename } from '@/lib/utils/formatters'

/** Converts SCREAMING_SNAKE or snake_case status codes to Title Case labels. */
export function formatStatusLabel(value: string): string {
  const text = String(value ?? '').trim()
  if (!text) return ''
  return text
    .toLowerCase()
    .split('_')
    .map((chunk) => (chunk ? chunk[0].toUpperCase() + chunk.slice(1) : chunk))
    .join(' ')
}

/** Last calendar day of the current month as `yyyy-MM-dd`. */
export function endOfMonthIsoToday(): string {
  const d = new Date()
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const yyyy = last.getFullYear()
  const mm = String(last.getMonth() + 1).padStart(2, '0')
  const dd = String(last.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function toDate(input: string | number | Date): Date {
  if (input instanceof Date) return input

  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return new Date('')
    return new Date(input < 1_000_000_000_000 ? input * 1000 : input)
  }

  if (typeof input !== 'string') return new Date('')
  const trimmed = input.trim()
  if (trimmed === '') return new Date('')

  if (/^\d+$/.test(trimmed)) {
    const raw = Number(trimmed)
    if (Number.isFinite(raw)) {
      return new Date(trimmed.length <= 10 ? raw * 1000 : raw)
    }
  }

  // ISO date-only format (YYYY-MM-DD): parse as local midnight to avoid UTC shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number)
    return new Date(y, m - 1, d)
  }

  return new Date(trimmed)
}
